import { InputSummaryItem, InputSummary } from '../../input-summary';
import InputSummaryUtils from '../../../helper/input-summary/input-summary-util';
import { UnitValue } from '../../data-structures/unit-value.model';

/**
 * An interface used to define the class structure for the feedstock npk inputs
 */
export interface FeedstockNpkInterface {
  nitrogen: UnitValue;
  phosphorus: UnitValue;
  potassium: UnitValue;
  ammoniaNitrogen: UnitValue;
}

/**
 * A class that contains members for the feedstock npk inputs
 */
export class FeedstockNpk {
  nitrogen: UnitValue;
  phosphorus: UnitValue;
  potassium: UnitValue;
  ammoniaNitrogen: UnitValue;

  /**
   * constructs the FeedstockNpk class.
   * @param param is used to construct the class. If null or not provided, the class will be set to default values
   */
  constructor(param: FeedstockNpkInterface = {
    nitrogen: new UnitValue(),
    phosphorus: new UnitValue(),
    potassium: new UnitValue(),
    ammoniaNitrogen: new UnitValue(),
  }) {
    this.nitrogen = new UnitValue(param.nitrogen);
    this.phosphorus = new UnitValue(param.phosphorus);
    this.potassium = new UnitValue(param.potassium);
    this.ammoniaNitrogen = new UnitValue(param.ammoniaNitrogen);
  }

  /**
   * returns a separate instance of the provided FeedstockNpk obj
   * @param feedstockNpk will be returned as a separate instance
   */
  static deepCopy(feedstockNpk: FeedstockNpk) {
    let copy;
    if (feedstockNpk) {
      copy = new FeedstockNpk(feedstockNpk);
      return copy;
    }
    throw new Error('unable to perform deep copy of FeedstockNpk');
  }

  getInputSummary(path: string[]) {
    const inputSummary = new InputSummary();
    inputSummary.inputs.push(... this.getSummaryInputs(path));
    return inputSummary;
  }

  /**
   * Returns an array of InputSummaryItem containing inputs for each member of the FeedstockNpk class
   */
  getSummaryInputs(path: string[]) {
    let inputs: InputSummaryItem[] = [];
    inputs = InputSummaryUtils.performPush(inputs, FeedstockNpkPropName.nitrogen, this.nitrogen, [...path, 'nitrogen']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockNpkPropName.phosphorus, this.phosphorus, [...path, 'phosphorus']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockNpkPropName.potassium, this.potassium, [...path, 'potassium']);
    inputs = InputSummaryUtils.performPush(inputs, FeedstockNpkPropName.ammoniaNitrogen, this.ammoniaNitrogen, [...path, 'ammoniaNitrogen']);
    return inputs;
  }
}

/**
 * An enum that contains a proper display name for properties of the FeedstockNpk class.
 *
 * ALL MEMBERS ARE INTENTIONALLY CAMELCASE FOR OBJ REFERENCING
 */
export enum FeedstockNpkPropName {
  nitrogen= 'INPUT_SUMMARY.FEEDSTOCK.NPK.NITROGEN',
  phosphorus = 'INPUT_SUMMARY.FEEDSTOCK.NPK.PHOSPHORUS',
  potassium = 'INPUT_SUMMARY.FEEDSTOCK.NPK.POTASSIUM',
  ammoniaNitrogen = 'INPUT_SUMMARY.FEEDSTOCK.NPK.AMMONIA_NITROGEN',
  id_nitrogen= 'nitrogen',
  id_phosphorus = 'phosphorus',
  id_potassium = 'potassium',
  id_ammoniaNitrogen = 'ammonia-nitrogen'
}
